// Shared colour-contrast helpers. The same isWhiteOrShade logic previously lived
// inline in Header.tsx (and a weaker copy in TermsModal.tsx); it is centralised
// here now that the PWA install bar and modal need it too (DRY — see hub
// CLAUDE.md §2d). This implementation mirrors Header's robust version: it parses
// #rgb / #rrggbb / rgb() / rgba() and treats anything at or above the near-white
// threshold as "light", so pale tenant brand colours (e.g. #f0f0f0) correctly
// get dark foreground text instead of unreadable white-on-near-white.

// Channel value at or above which all three channels count as near-white.
const NEAR_WHITE_THRESHOLD = 235;

// Parse a CSS colour string into [r, g, b], or null if it isn't a form we
// recognise (the keyword "white", #rgb, #rrggbb, rgb()/rgba()).
function parseRgb(colour: string): [number, number, number] | null {
  if (!colour) return null;
  const s = colour.trim().toLowerCase();
  if (s === "white") return [255, 255, 255];

  if (s.startsWith("#")) {
    let hex = s.slice(1);
    if (hex.length === 3) {
      // expand `#abc` → `#aabbcc`
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }
    if (hex.length !== 6 || !/^[0-9a-f]{6}$/.test(hex)) return null;
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }

  const m = s.match(
    /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/
  );
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

// True when the colour is white or a near-white shade, so callers can swap to a
// dark foreground for legibility on a pale brand colour.
export function isWhiteOrShade(colour: string): boolean {
  const rgb = parseRgb(colour);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return (
    r >= NEAR_WHITE_THRESHOLD &&
    g >= NEAR_WHITE_THRESHOLD &&
    b >= NEAR_WHITE_THRESHOLD
  );
}

// Pick a colour that stays visible on a white surface (e.g. a modal card):
// prefer `primary`, fall back to `secondary`, then a dark default, skipping any
// white/near-white option that would vanish against white.
export function visibleAccent(
  primary: string,
  secondary?: string,
  fallback = "#1e1b4b"
): string {
  if (primary && !isWhiteOrShade(primary)) return primary;
  if (secondary && !isWhiteOrShade(secondary)) return secondary;
  return fallback;
}

// Foreground colour that reads against `background`: an explicit theme override
// wins, otherwise dark text on a light background and the supplied fallback
// (usually the theme text colour) on a dark one.
export function readableTextColour(
  background: string,
  override?: string,
  fallback = "#ffffff"
): string {
  if (override) return override;
  return isWhiteOrShade(background) ? "#000000" : fallback;
}
